import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDemand } from '../../context/DemandContext';
import { useFileManagementContext } from '../../context/FileManagementContext';
import { useGroupQuery } from '../useOSApi';
import { GROUP_NAME } from '../../config/constants';
import { message } from 'antd';

/**
 * 🎯 費用試算 Hook (Peace 版本 - 統一後台實現)
 * 統一管理價目表、影音時長收集、費用計算邏輯
 */
export const usePriceCalculation = () => {
  const { state: demandState } = useDemand();
  const { state: fileState } = useFileManagementContext();

  // 📊 價目表狀態
  const [priceList, setPriceList] = useState({
    videoItems: [
      { name: '3min-video', spec: "3分鐘(含)內", price: 25000 },
      { name: '3-5min-video', spec: "3~5分鐘", price: 50000 },
      { name: '5-10min-video', spec: "5~10分鐘", price: 75000 },
      { name: '10min-video', spec: "10分鐘以上", price: 100000 }
    ],
    actionItems: [
      { name: 'sit', spec: "坐下", price: 1000 },
      { name: 'stand', spec: "站起", price: 1000 },
      { name: 'turn', spec: "轉身", price: 1000 },
      { name: 'bow', spec: "揖手", price: 1000 },
      { name: 'salute', spec: "敬禮", price: 1000 },
      { name: 'other', spec: "其他", price: 2000 }
    ]
  });

  const [showCostCalculation, setShowCostCalculation] = useState(false);
  const [isPriceListLoading, setIsPriceListLoading] = useState(false);
  const [autoCalculationEnabled, setAutoCalculationEnabled] = useState(true);

  // 🎯 查詢價目表
  const { data: priceListGroupData } = useGroupQuery({
    groupName: GROUP_NAME.PRICE_LIST,
    queryType: 'detailsByName',
    enabled: true
  });

  // 🔧 輔助函數：根據 fileId 獲取完整檔案信息（包含 duration）
  const getFileInfoById = useCallback((fileObj) => {
    // 如果是字串，解析為物件
    if (typeof fileObj === 'string') {
      try {
        fileObj = JSON.parse(fileObj);
      } catch (e) {
        console.warn('解析檔案字串失敗:', fileObj);
        return null;
      }
    }

    // 檢查是否有 fileId
    if (fileObj && fileObj.fileId) {
      // 這裡暫時返回原物件，等等我們會從 FileManagementContext 取得更多資訊
      return fileObj;
    }

    return fileObj;
  }, []);

  // 🎯 費用計算結果狀態（不自動計算，等待手動觸發）
  const [mediaDurations, setMediaDurations] = useState({
    public: 0,
    family: 0,
    farewell: 0,
    actions: []
  });

  // 🔧 計算單個區域的檔案時長 - created 優先於 my
  const calculateSectionDuration = useCallback((sectionFiles) => {
    const getFileDuration = (fileObjects) => {
      if (!Array.isArray(fileObjects)) {
        return 0;
      }
      return fileObjects.reduce((total, fileObj) => {
        const processedFile = getFileInfoById(fileObj);
        // 🎯 簡化：直接使用上傳時已計算好的 duration
        if (processedFile && processedFile.duration && typeof processedFile.duration === 'number') {
          return total + processedFile.duration;
        }
        return total; // 移除預設值，上傳時已處理完成
      }, 0);
    };

    if (!sectionFiles) return 0;

    // 🎯 優先級：created > my（包含 video 和 audio）
    const createdVideo = sectionFiles.created?.video || [];
    const createdAudio = sectionFiles.created?.audio || [];
    const myVideo = sectionFiles.my?.video || [];
    const myAudio = sectionFiles.my?.audio || [];

    // 先計算 created 檔案的總時長
    const createdDuration = getFileDuration([...createdVideo, ...createdAudio]);

    // 如果有 created 檔案，直接返回
    if (createdDuration > 0) {
      return createdDuration;
    }

    // 沒有 created 檔案，才使用 my 檔案
    const myDuration = getFileDuration([...myVideo, ...myAudio]);
    return myDuration;
  }, [getFileInfoById]);

  // 🔧 計算所有區域的檔案時長
  const calculateAllDurations = useCallback(() => {
    const ceremony = demandState.ceremony;
    if (!ceremony) return;

    console.log('=== 開始費用計算（created > my 優先級） ===');

    // 計算各個區域
    const publicDuration = calculateSectionDuration(ceremony.public?.files);
    const familyDuration = calculateSectionDuration(ceremony.family?.files);
    const farewellDuration = calculateSectionDuration(ceremony.farewell?.files);

    // 計算每individual action 的時長
    const actionDurations = (ceremony.actions || []).map((action, index) => {
      const actionDuration = calculateSectionDuration(action.files);
      return {
        id: action.id,
        index,
        duration: actionDuration
      };
    });

    const newDurations = {
      public: publicDuration,
      family: familyDuration,
      farewell: farewellDuration,
      actions: actionDurations
    };

    console.log('計算結果:', newDurations);
    setMediaDurations(newDurations);

    return newDurations;
  }, [demandState.ceremony, calculateSectionDuration]);

  // 🎯 根據影音時長計算費用（影片優先）
  const calculateCost = useCallback((mediaSeconds) => {
    if (mediaSeconds === 0) {
      return {
        price: 0,
        time: "尚未上傳影音"
      };
    }

    const minutes = mediaSeconds / 60;
    let videoItemIndex = 0;

    if (minutes <= 3) {
      videoItemIndex = 0;
    } else if (minutes <= 5) {
      videoItemIndex = 1;
    } else if (minutes <= 10) {
      videoItemIndex = 2;
    } else {
      videoItemIndex = 3;
    }

    const videoPrice = priceList.videoItems[videoItemIndex]?.price || 25000;
    const timeText = priceList.videoItems[videoItemIndex]?.spec || "3分鐘(含)內";

    return {
      price: videoPrice,
      time: timeText
    };
  }, [priceList.videoItems]);

  // 🎯 費用計算結果狀態（不自動計算）
  const [cost, setCost] = useState({
    public: { price: 0, info: '尚未計算' },
    family: { price: 0, info: '尚未計算' },
    farewell: { price: 0, info: '尚未計算' },
    actions: [],
    total: 0
  });

  // 🔧 計算完整費用結構
  const calculateAllCosts = useCallback((durations) => {
    if (!durations) return;

    const publicCost = calculateCost(durations.public);
    const familyCost = calculateCost(durations.family);
    const farewellCost = calculateCost(durations.farewell);

    console.log('=== 費用計算結果 ===');
    console.log('當前價目表:', priceList);
    console.log('影音時長:', durations);
    console.log('public 費用:', publicCost);
    console.log('family 費用:', familyCost);
    console.log('farewell 費用:', farewellCost);

    // 動作費用計算
    const actionTextMap = {
      "sit": "坐下",
      "stand": "站起",
      "turn": "轉身",
      "bow": "揖手",
      "salute": "敬禮",
      "other": "其他"
    };

    const actions = demandState.ceremony?.actions || [];
    const actionCosts = actions.map((action, index) => {
      const actionType = action.action;
      const actionInfo = actionTextMap[actionType] || '';
      const actionItem = priceList.actionItems.find(item => item.name === actionType);
      const actionCost = actionType ? (actionItem?.price || 0) : 0;

      // 從 durations.actions 中找到對應的時長費用
      const actionDurationData = durations.actions.find(a => a.id === action.id);
      const actionDurationCost = actionDurationData ? calculateCost(actionDurationData.duration) : { price: 0, time: '尚未上傳影音' };

      return {
        id: action.id,
        order: index + 1,
        actionType,
        price: actionCost + actionDurationCost.price, // 動作費用 + 時長費用
        info: actionInfo,
        duration: actionDurationData?.duration || 0,
        durationInfo: actionDurationCost.time
      };
    });

    // 計算動作總費用
    const totalActionCost = actionCosts.reduce((total, action) => total + action.price, 0);

    // 計算總費用
    const totalCost = publicCost.price + familyCost.price + farewellCost.price + totalActionCost;

    const result = {
      public: {
        price: publicCost.price,
        info: publicCost.time
      },
      family: {
        price: familyCost.price,
        info: familyCost.time
      },
      farewell: {
        price: farewellCost.price,
        info: farewellCost.time
      },
      actions: actionCosts,
      total: totalCost,
    };

    console.log('最終費用結果:', result);
    setCost(result);

    return result;
  }, [calculateCost, priceList, demandState.ceremony?.actions]);

  // 🎯 載入價目表數據
  const fetchPriceList = useCallback(async () => {
    setIsPriceListLoading(true);
    try {
      // 從群組數據中提取價格表項目
      const pricelistItem = priceListGroupData?.data?.[0]?.items?.[0];

      if (pricelistItem?.information) {
        const { videoItems, actionItems } = pricelistItem.information;

        if (videoItems && actionItems) {
          setPriceList({ videoItems, actionItems });
        }
      }
    } catch (error) {
      console.error('獲取價目表數據失敗:', error);
    } finally {
      setIsPriceListLoading(false);
    }
  }, [priceListGroupData]);

  // 🎯 自動載入價目表
  useEffect(() => {
    fetchPriceList();
  }, [fetchPriceList]);

  // 🎯 監聽檔案變化，自動觸發費用計算
  useEffect(() => {
    if (!autoCalculationEnabled) return;

    // 檢查是否有已完成的媒體檔案
    const hasCompletedMediaFiles = fileState.some(file =>
      file.status === 'done' &&
      file.duration &&
      (file.content_type?.startsWith('audio/') || file.content_type?.startsWith('video/'))
    );

    // 檢查 demandState 中是否有檔案
    const hasAnyFiles =
      demandState.ceremony?.public?.files?.my?.audio?.length > 0 ||
      demandState.ceremony?.family?.files?.my?.audio?.length > 0 ||
      demandState.ceremony?.farewell?.files?.my?.audio?.length > 0 ||
      demandState.ceremony?.actions?.some(action => action.files?.my?.photo?.length > 0);

    if (hasCompletedMediaFiles || hasAnyFiles) {
      console.log('🔄 檔案變化偵測到，自動觸發費用計算...');

      // 延遲執行，確保所有狀態更新完成
      const timeoutId = setTimeout(() => {
        const durations = calculateAllDurations();
        if (durations) {
          calculateAllCosts(durations);
          setShowCostCalculation(true);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [
    fileState,
    demandState.ceremony,
    autoCalculationEnabled,
    calculateAllDurations,
    calculateAllCosts
  ]);

  // 🎯 手動觸發費用計算
  const handleCostCalculation = useCallback(() => {
    console.log('🔧 開始手動費用計算...');

    // 1. 計算所有區域的檔案時長
    const durations = calculateAllDurations();

    // 2. 根據時長計算費用
    if (durations) {
      const finalCost = calculateAllCosts(durations);

      // 3. 顯示計算結果
      setShowCostCalculation(true);

      // 4. 顯示成功消息和總費用
      message.success(`費用計算完成！總費用：${finalCost.total.toLocaleString()} 元`, 3);

      console.log('✅ 費用計算完成:', finalCost);
      return finalCost;
    } else {
      message.warning('無法獲取檔案信息，請稍後再試');
    }
  }, [calculateAllDurations, calculateAllCosts]);

  return {
    // 狀態
    priceList,
    mediaDurations,
    cost,
    showCostCalculation,
    isPriceListLoading,

    // 操作
    handleCostCalculation,
    fetchPriceList,
    setShowCostCalculation,
    setAutoCalculationEnabled,

    // 輔助數據
    hasAnyMedia: mediaDurations.public > 0 || mediaDurations.family > 0 || mediaDurations.farewell > 0 || mediaDurations.actions.some(a => a.duration > 0)
  };
}; 